import dynamic from 'next/dynamic';
import {
  HotelTileGroupContainer,
  PopularHotelsContainer,
  SpecialOffersContainer,
  HolidaysCallToBookContainer
} from '@components/content/fit';

import {
  BenefitsGroupContainer,
  BreadcrumbContainer,
  CallToBookContainer,
  CarouselContainer,
  ExpandableListContainer,
  GridContainer,
  GridItem,
  HeroContainer,
  ImageContainer,
  IntroContainer,
  PageSeoContainer,
  RecentlyViewedContainer,
  Tab,
  TabGroupContainer,
  TestimonialsContainer,
  TextContainer,
  TitleContainer,
  TrustPilotContainer,
  VideoContainer
} from '@components/content/common';
import {
  ClimateDataContainer,
  DestinationFaqContainer,
  DestinationListContainer,
  DestinationsGroupContainer,
  ResortsListContainer,
  TestimonialContentContainer,
  TravelGuideContainer,
  VideoGuideContainer
} from '@components/content/destination';
import { SimilarArticlesContainer } from '@components/content/guide';
import { DiscoverTheWorld, SearchBarContainer, TripTypeTilesContainer } from '@components/content/homepage';
import {
  InfoRowGroupContainer,
  MediaEnquiriesContainer,
  OnlineEnquiryContainer,
  OrderBrochureContainer,
  ContactDetailsContainer,
  CharityTileContainer
} from '@components/content/information';
import { TourDestinationContainer, TourGroupContainer, ToursCallToBookContainer } from '@components/content/tours';
import { RecentlyViewedCarouselContainer } from '@components/recently-viewed/RecentlyViewedCarouselContainer';
import { withSeparators } from '@components/hoc/dynamic-components';
import { Separator } from '@styles/layout';

const DynamicAllInclusiveOffersCountryList = dynamic(
  () =>
    import('../../src/components/content/offers/AllInclusiveOffersCountryListContainer').then(
      (e) => e.AllInclusiveOffersCountryListContainer
    ),
  {
    ssr: false
  }
);

const DynamicPageTileGroupContainer = dynamic(
  () => import('../components/content/offers/PageTileGroupContainer').then((e) => e.PageTileGroupContainer),
  {
    ssr: false
  }
);

const DynamicTeamMember = dynamic(
  () => import('../components/agents/team-member/TeamMember').then((e) => e.TeamMemberComponent),
  {
    ssr: false
  }
);

const DynamicDownloadableFiles = dynamic(
  () => import('../components/agents/downloadable-files/DownloadableFiles').then((e) => e.DownloadableFiles),
  {
    ssr: false
  }
);

const DynamicIncentiveTiles = dynamic(
  () => import('../components/agents/incentive-tiles/IncentiveTiles').then((e) => e.IncentiveTiles),
  {
    ssr: false
  }
);
export enum ComponentHandles {
  AGENT_TEAM_MEMBER = 'agentTeamMember',
  AGENT_INCENTIVE_TILES = 'agentIncentiveTiles',
  ALL_INCLUSIVE_OFFERS_COUNTRY_LIST = 'allInclusiveOffersCountryList',
  BENEFIT_GROUP = 'benefitGroup',
  BREADCRUMBS = 'breadcrumbs',
  CALL_TO_BOOK = 'callToBook',
  CAROUSEL = 'carousel',
  CHARITY_TILE = 'charityTile',
  CLIMATE_DATA = 'climateData',
  CONTACT_DETAILS = 'contactDetails',
  DESTINATION_GROUP = 'destinationGroup',
  DESTINATION_FAQ = 'destinationFaq',
  DESTINATION_LIST = 'destinationList',
  DISCOVER_THE_WORLD = 'discoverTheWorld',
  DOWNLOADABLE_FILE = 'downloadableFile',
  EXPANDABLE_LIST_GROUP = 'expandableListGroup',
  EXPLORE_DESTINATION = 'exploreDestination',
  GRID = 'grid',
  GRID_CONTAINER = 'gridContainer',
  HERO_IMAGE = 'heroImage',
  SEARCH_BAR = 'holidaySearchBar',
  HOLIDAYS_CALL_TO_BOOK = 'holidaysCallToBook',
  HOTEL_TILE_GROUP = 'hotelTileGroup',
  IMAGE = 'image',
  INFO_ROW_GROUP = 'infoRowGroup',
  INTRO = 'intro',
  MEDIA_ENQUIRIES = 'mediaEnquiries',
  ONLINE_ENQUIRY = 'onlineEnquiry',
  ORDER_BROCHURE = 'orderBrochure',
  PAGE_SEO = 'pageSeo',
  PAGE_TILE_GROUP = 'pageTileGroup',
  POPULAR_HOTELS = 'popularHotels',
  RECENTLY_VIEWED = 'recentlyViewed',
  RESORTS_LIST = 'resortsList',
  SEPARATOR = 'separator',
  SPECIAL_OFFERS = 'specialOffers',
  SIMILAR_ARTICLES = 'similarArticles',
  TAB = 'tab',
  TAB_GROUP = 'tabGroup',
  TESTIMONIALS = 'testimonials',
  TESTIMONIALS_COMPONENT = 'testimonialsComponent',
  TEXT = 'text',
  TITLE = 'title',
  TOUR_DESTINATION = 'tourDestination',
  TOUR_GROUP = 'tourGroup',
  TOURS_CALL_TO_BOOK = 'toursCallToBook',
  TRAVEL_GUIDE_TILES = 'travelGuideTiles',
  TRIP_TYPE_TILES = 'tripTypeTile',
  TRUST_PILOT = 'trustPilot',
  YOUR_RECENTLY_VIEWED = 'yourRecentlyReviewed',
  VIDEO = 'video',
  VIDEO_GUIDE_TILES = 'videoGuideTiles'
}

export const ComponentTypes = new Map();

ComponentTypes.set(ComponentHandles.AGENT_TEAM_MEMBER, DynamicTeamMember);
ComponentTypes.set(ComponentHandles.AGENT_INCENTIVE_TILES, DynamicIncentiveTiles);
ComponentTypes.set(ComponentHandles.ALL_INCLUSIVE_OFFERS_COUNTRY_LIST, DynamicAllInclusiveOffersCountryList);
ComponentTypes.set(ComponentHandles.BENEFIT_GROUP, withSeparators(BenefitsGroupContainer));
ComponentTypes.set(ComponentHandles.BREADCRUMBS, withSeparators(BreadcrumbContainer));
ComponentTypes.set(ComponentHandles.CALL_TO_BOOK, withSeparators(CallToBookContainer));
ComponentTypes.set(ComponentHandles.CAROUSEL, withSeparators(CarouselContainer));
ComponentTypes.set(ComponentHandles.CHARITY_TILE, withSeparators(CharityTileContainer));
ComponentTypes.set(ComponentHandles.CLIMATE_DATA, withSeparators(ClimateDataContainer));
ComponentTypes.set(ComponentHandles.CONTACT_DETAILS, withSeparators(ContactDetailsContainer));
ComponentTypes.set(ComponentHandles.DESTINATION_GROUP, withSeparators(DestinationsGroupContainer));
ComponentTypes.set(ComponentHandles.DESTINATION_FAQ, withSeparators(DestinationFaqContainer));
ComponentTypes.set(ComponentHandles.DESTINATION_LIST, withSeparators(DestinationListContainer));
ComponentTypes.set(ComponentHandles.DISCOVER_THE_WORLD, withSeparators(DiscoverTheWorld));
ComponentTypes.set(ComponentHandles.DOWNLOADABLE_FILE, DynamicDownloadableFiles);
ComponentTypes.set(ComponentHandles.EXPANDABLE_LIST_GROUP, withSeparators(ExpandableListContainer));
ComponentTypes.set(ComponentHandles.EXPLORE_DESTINATION, withSeparators(ResortsListContainer));
ComponentTypes.set(ComponentHandles.GRID, withSeparators(GridItem));
ComponentTypes.set(ComponentHandles.GRID_CONTAINER, withSeparators(GridContainer));
ComponentTypes.set(ComponentHandles.HERO_IMAGE, withSeparators(HeroContainer));
ComponentTypes.set(ComponentHandles.SEARCH_BAR, withSeparators(SearchBarContainer));
ComponentTypes.set(ComponentHandles.HOLIDAYS_CALL_TO_BOOK, withSeparators(HolidaysCallToBookContainer));
ComponentTypes.set(ComponentHandles.HOTEL_TILE_GROUP, withSeparators(HotelTileGroupContainer));
ComponentTypes.set(ComponentHandles.IMAGE, withSeparators(ImageContainer));
ComponentTypes.set(ComponentHandles.INFO_ROW_GROUP, withSeparators(InfoRowGroupContainer));
ComponentTypes.set(ComponentHandles.INTRO, withSeparators(IntroContainer));
ComponentTypes.set(ComponentHandles.HOTEL_TILE_GROUP, withSeparators(HotelTileGroupContainer));
ComponentTypes.set(ComponentHandles.MEDIA_ENQUIRIES, withSeparators(MediaEnquiriesContainer));
ComponentTypes.set(ComponentHandles.ONLINE_ENQUIRY, withSeparators(OnlineEnquiryContainer));
ComponentTypes.set(ComponentHandles.ORDER_BROCHURE, withSeparators(OrderBrochureContainer));
ComponentTypes.set(ComponentHandles.PAGE_SEO, PageSeoContainer);
ComponentTypes.set(ComponentHandles.PAGE_TILE_GROUP, DynamicPageTileGroupContainer);
ComponentTypes.set(ComponentHandles.POPULAR_HOTELS, withSeparators(PopularHotelsContainer));
ComponentTypes.set(ComponentHandles.RECENTLY_VIEWED, withSeparators(RecentlyViewedCarouselContainer));
ComponentTypes.set(ComponentHandles.RESORTS_LIST, withSeparators(ResortsListContainer));
ComponentTypes.set(ComponentHandles.SEPARATOR, withSeparators(Separator));
ComponentTypes.set(ComponentHandles.SIMILAR_ARTICLES, withSeparators(SimilarArticlesContainer));
ComponentTypes.set(ComponentHandles.SPECIAL_OFFERS, withSeparators(SpecialOffersContainer));
ComponentTypes.set(ComponentHandles.TAB, Tab);
ComponentTypes.set(ComponentHandles.TAB_GROUP, withSeparators(TabGroupContainer));
ComponentTypes.set(ComponentHandles.TESTIMONIALS, withSeparators(TestimonialsContainer));
ComponentTypes.set(ComponentHandles.TESTIMONIALS_COMPONENT, withSeparators(TestimonialContentContainer));
ComponentTypes.set(ComponentHandles.TEXT, withSeparators(TextContainer));
ComponentTypes.set(ComponentHandles.TITLE, withSeparators(TitleContainer));
ComponentTypes.set(ComponentHandles.TOUR_DESTINATION, withSeparators(TourDestinationContainer));
ComponentTypes.set(ComponentHandles.TOUR_GROUP, withSeparators(TourGroupContainer));
ComponentTypes.set(ComponentHandles.TOURS_CALL_TO_BOOK, withSeparators(ToursCallToBookContainer));
ComponentTypes.set(ComponentHandles.TRAVEL_GUIDE_TILES, withSeparators(TravelGuideContainer));
ComponentTypes.set(ComponentHandles.TRIP_TYPE_TILES, withSeparators(TripTypeTilesContainer));
ComponentTypes.set(ComponentHandles.TRUST_PILOT, withSeparators(TrustPilotContainer));
ComponentTypes.set(ComponentHandles.YOUR_RECENTLY_VIEWED, withSeparators(RecentlyViewedContainer));
ComponentTypes.set(ComponentHandles.VIDEO, withSeparators(VideoContainer));
ComponentTypes.set(ComponentHandles.VIDEO_GUIDE_TILES, withSeparators(VideoGuideContainer));
